// Recognize editor and unsafe HTML tags while preserving legacy placeholders such as <patient>.
// Keep supported formatting tags aligned with the TEXT sanitizer; literal known tags remain ambiguous.
const questionLabelHtmlPattern =
  /<\/?(?:p|br|strong|b|u|script|style|template|head|iframe|object|embed|img|picture|svg|image|video|audio|textarea|option|noscript|div|span|h[1-6]|ul|ol|li|a|em|i|s|strike|mark|blockquote|hr)(?=[\s/>])[^>]*>/i;

export const hasQuestionLabelHtml = (value: string): boolean =>
  questionLabelHtmlPattern.test(value);

export const hiddenRichTextTags = [
  'script',
  'style',
  'template',
  'head',
  'iframe',
  'object',
  'embed',
  'textarea',
  'option',
  'noscript',
];

/** Remove hidden contents when extracting visible text or appending a label suffix. */
export const parseQuestionLabelHtml = (value: string): Document => {
  const doc = new DOMParser().parseFromString(value, 'text/html');
  doc
    .querySelectorAll(hiddenRichTextTags.join(', '))
    .forEach((node) => node.remove());
  return doc;
};

/** Plain text for validation, accessible names and compact builder summaries. */
export const questionLabelText = (value: string = ''): string => {
  if (!hasQuestionLabelHtml(value)) return value;
  const doc = parseQuestionLabelHtml(value);
  doc
    .querySelectorAll('p, div, li, h1, h2, h3, h4, h5, h6, br')
    .forEach((node) => {
      node.before(doc.createTextNode(' '));
      node.after(doc.createTextNode(' '));
    });
  return (doc.body.textContent ?? '').replace(/\s+/gu, ' ').trim();
};

/**
 * Append a literal suffix to the last label paragraph without changing its formatting.
 * This does not sanitize the existing HTML; render it through RichTextPreview.
 */
export const appendQuestionLabelText = (
  value: string,
  suffix: string
): string => {
  const isHtml = hasQuestionLabelHtml(value);
  if (!isHtml && !hasQuestionLabelHtml(suffix)) return value + suffix;
  const doc = parseQuestionLabelHtml(isHtml ? value : '<p></p>');
  const lastNode = doc.body.lastChild;
  const target = lastNode?.nodeName === 'P' ? lastNode : doc.body;
  target.appendChild(doc.createTextNode(isHtml ? suffix : value + suffix));
  return doc.body.innerHTML;
};

/** Whether a label contains visible text, including legacy formatted labels. */
export const hasVisibleQuestionLabel = (value: unknown): boolean =>
  typeof value === 'string' &&
  !!questionLabelText(value).replace(
    /[\s\u200B-\u200D\u2060\u00AD\uFEFF]/gu,
    ''
  );

/** Keep the source label when its translation is missing or has no visible text. */
export const getTranslatedQuestionLabel = (
  field: {
    label: string;
    translations?: Record<string, { label?: string }> | null;
  },
  language: string,
  sourceLanguage: string
): string => {
  if (!field.label || !language || language === sourceLanguage)
    return field.label;
  const translatedLabel = field.translations?.[language]?.label ?? '';
  return hasVisibleQuestionLabel(translatedLabel)
    ? translatedLabel
    : field.label;
};
